import React from 'react';
import { motion } from 'framer-motion';
import { ClosingLine, InteriorSlide, SlideTitle } from './SlideShell';
import type { SlideProps } from './SlideShell';
import { peerAnswers, peerSteps } from '../../data/snakkerStory';
const EASE = [0.23, 1, 0.32, 1] as const;
export function SlidePeer({}: SlideProps) {
  return <InteriorSlide surface data-id="element-500" data-mp="29l1av37qwcy7">
      <SlideTitle eyebrow="Optional peer mode · an extension" data-id="element-501" data-mp="29lt0lxenvggk">
        From interaction to <span className="text-[var(--brand)]" data-id="element-502" data-mp="377752p6ihhjr">peer insight.</span>
      </SlideTitle>

      <div className="mt-[clamp(1.25rem,2.5vw,2.25rem)] flex min-h-0 flex-1 items-stretch gap-[clamp(1.25rem,3vw,3rem)]" data-id="element-503" data-mp="329yjeem5mvdr">
        <div className="flex min-h-0 flex-1 gap-[clamp(0.6rem,1.2vw,1rem)]" data-id="element-504" data-mp="2xg44dohhv4l0">
          <motion.div initial={{
          opacity: 0,
          y: 18
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.15,
          duration: 0.5
        }} className="flex min-h-0 flex-1 flex-col rounded-2xl bg-white p-[clamp(1rem,2vw,1.85rem)] ring-1 ring-[var(--fg)]/[0.06]" data-id="element-505" data-mp="2si2jcy0icv6w">
            <p className="font-mono text-[clamp(0.56rem,0.72vw,0.68rem)] uppercase tracking-[0.18em] text-[var(--muted)]" data-id="element-506" data-mp="36mhv4016ci0r">You answered…</p>
            <p className="mt-[clamp(0.75rem,1.5vw,1.25rem)] text-[clamp(1.1rem,1.9vw,1.75rem)] font-semibold leading-[1.1] tracking-[-0.03em] text-ink" data-id="element-507" data-mp="2ww6epe0b0gcz">
              Same goal. Same starting approach?
            </p>
            <div className="mt-auto grid grid-cols-2 gap-2 pt-[clamp(1rem,2vw,1.5rem)]" data-id="element-508" data-mp="2nuysvvez57zs">
              <span className="rounded-lg border border-ink/15 py-2.5 text-center text-[clamp(0.72rem,0.95vw,0.9rem)] font-semibold tracking-[0.1em] text-graphite" data-id="element-509" data-mp="2rwtw55ggtvg7">YES</span>
              <span className="rounded-lg bg-ink py-2.5 text-center text-[clamp(0.72rem,0.95vw,0.9rem)] font-semibold tracking-[0.1em] text-white" data-id="element-510" data-mp="2dzne5d3kg0s1">NO</span>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 18
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.35,
          duration: 0.5
        }} className="flex min-h-0 flex-1 flex-col rounded-2xl bg-[var(--fg)] p-[clamp(1rem,2vw,1.85rem)] text-white" data-id="element-511" data-mp="c3uth4og4l3n">
            <p className="font-mono text-[clamp(0.56rem,0.72vw,0.68rem)] uppercase tracking-[0.18em] text-[var(--brand-lighter)]" data-id="element-512" data-mp="aqv7pvg0c7cd">Then…</p>
            <p className="mt-[clamp(0.75rem,1.5vw,1.25rem)] text-[clamp(1.1rem,1.9vw,1.75rem)] font-semibold leading-[1.1] tracking-[-0.03em]" data-id="element-513" data-mp="r48xhkez66h">
              Here is how other fertility specialists answered the same question.
            </p>
            <ul className="mt-auto space-y-3 pt-[clamp(1rem,2vw,1.5rem)]" data-id="element-514" data-mp="tfrxpn4dgdos">
              {peerAnswers.map((a, i) => <li key={a.label} data-id="element-515" data-mp="om6zbtyv9hp2" data-mpi={i}>
                  <div className="flex items-baseline justify-between text-[clamp(0.72rem,0.95vw,0.9rem)] font-semibold" data-id="element-516" data-mp="jq04oxbx7smf">
                    <span className={a.you ? 'text-white' : 'text-white/60'} data-id="element-517" data-mp="1h5mcwbc5yec7">
                      {a.label}
                      {a.you && <span className="ml-2 rounded bg-white/15 px-1.5 py-0.5 text-[0.65em] uppercase tracking-[0.12em]" data-id="element-518" data-mp="1cct6lj11qxy0">You</span>}
                    </span>
                    <span className="tabular-nums" data-id="element-519" data-mp="1onll36iisoqe">{a.share}%</span>
                  </div>
                  <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-white/10" data-id="element-520" data-mp="1tmx30iumi09k">
                    <motion.div className={`h-full rounded-full ${a.you ? 'bg-[var(--brand-lighter)]' : 'bg-white/40'}`} initial={{
                  width: 0
                }} animate={{
                  width: `${a.share}%`
                }} transition={{
                  delay: 0.7 + i * 0.12,
                  duration: 0.8,
                  ease: EASE
                }} data-id="element-521" data-mp="1ym9rt9521hes" />
                  </div>
                </li>)}
            </ul>
            <p className="mt-3 font-mono text-[0.6rem] tracking-[0.1em] text-white/40" data-id="element-522" data-mp="2hbzknqp9bt42">Illustrative data</p>
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0,
        x: 18
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.5,
        duration: 0.5
      }} className="hidden w-[32%] flex-shrink-0 flex-col justify-center md:flex" data-id="element-523" data-mp="2maatby3tfxkp">
          <p className="text-[clamp(0.9rem,1.3vw,1.2rem)] font-medium leading-snug text-[var(--muted)]" data-id="element-524" data-mp="3j2hboj6smlsr">
            A Snak can also become a lightweight peer pulse where HCPs:
          </p>
          <ol className="mt-[clamp(0.75rem,1.4vw,1.2rem)] border-b border-[var(--fg)]/10" data-id="element-525" data-mp="3st25ltbiv25c">
            {peerSteps.map((s, i) => <li key={s} className="flex items-baseline gap-3 border-t border-[var(--fg)]/10 py-[clamp(0.45rem,0.9vw,0.75rem)]" data-id="element-526" data-mp="3549gogtbhuk8" data-mpi={i}>
                <span className="font-mono text-[clamp(0.58rem,0.75vw,0.7rem)] tracking-[0.18em] text-[var(--muted)]" data-id="element-527" data-mp="306zhwak1g6jq">0{i + 1}</span>
                <span className="text-[clamp(1.1rem,1.9vw,1.75rem)] font-extrabold tracking-[-0.035em] text-[var(--fg)]" data-id="element-528" data-mp="3a2ks2vz9gd4k">{s}</span>
              </li>)}
          </ol>
          <p className="mt-[clamp(0.75rem,1.4vw,1.2rem)] text-[clamp(0.9rem,1.3vw,1.2rem)] font-medium leading-snug text-[var(--muted)]" data-id="element-529" data-mp="pcvf6oi4b1ei">
            …and Ferring receives aggregated insight into clinical thinking.
          </p>
        </motion.div>
      </div>

      <ClosingLine delay={0.8} data-id="element-530" data-mp="11wimh6yigtnv">
        Market research becomes <span className="text-[var(--brand)]" data-id="element-531" data-mp="myq8ij8m4dbs">part of the engagement</span> rather than a separate survey.
      </ClosingLine>
    </InteriorSlide>;
}